import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import LeadForm from './LeadForm';
import { PLANS } from '../utils/product';

const LeadSignupContext = createContext(null);

export function useLeadSignup() {
  const ctx = useContext(LeadSignupContext);
  if (!ctx) {
    throw new Error('useLeadSignup must be used within LeadSignupProvider');
  }
  return ctx;
}

export function LeadSignupProvider({ children }) {
  const [state, setState] = useState({ open: false, plan: '' });

  const openSignup = useCallback((planId = '') => {
    const valid = PLANS.some((p) => p.id === planId) ? planId : '';
    setState({ open: true, plan: valid });
  }, []);

  const closeSignup = useCallback(() => {
    setState((s) => ({ ...s, open: false }));
  }, []);

  return (
    <LeadSignupContext.Provider value={{ openSignup, closeSignup, ...state }}>
      {children}
      <LeadSignupModal open={state.open} plan={state.plan} onClose={closeSignup} />
    </LeadSignupContext.Provider>
  );
}

function LeadSignupModal({ open, plan, onClose }) {
  useEffect(() => {
    if (!open) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  const selected = PLANS.find((p) => p.id === plan);

  return (
    <div
      className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-ink-900/50 backdrop-blur-sm"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-signup-title"
        className="bg-white w-full sm:max-w-lg sm:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-sm px-5 pt-5 pb-3 flex items-start justify-between gap-3 border-b border-ink-100">
          <div>
            <p className="eyebrow mb-1">Fretz Empresas</p>
            <h2 id="lead-signup-title" className="font-display text-xl font-bold text-ink-900">
              Quero assinar
            </h2>
            {selected && (
              <p className="mt-1 text-sm text-ink-500">
                {selected.name} · {selected.priceLabel}{selected.priceSuffix}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-ink-100 text-ink-500"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <div className="px-5 py-5">
          <LeadForm
            key={plan || 'none'}
            id="lead-signup-modal"
            compact
            initialPlan={plan}
            submitLabel="Quero assinar"
            showFallbacks={false}
          />
        </div>
      </div>
    </div>
  );
}
